"use client"

import React, { useActionState, useEffect } from "react"
import Form from "next/form"
import { useRouter } from "next/navigation"
import { updateCompanyAction, UpdateCompanyActionState } from "@/actions/profileActions"
import { LINE_OF_BUSINESSES } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { zodResolver } from "@hookform/resolvers/zod"
import { LoaderCircle, PlusIcon, SaveIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import { Controller, useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/components"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { UpdateCompanySchema } from "@/components/account/profile/profile"
import { FormRow } from "@/components/account/shared/FormRow"
import { UbAccountProfileCompanyViewStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

const resolver = zodResolver(UpdateCompanySchema)

interface AccountProfileCompanyProps {
  accountContext: AccountContext
  blok: UbAccountProfileCompanyViewStoryblok
}

export default function AccountProfileCompanyClient(props: AccountProfileCompanyProps) {
  const { setProfileUpdated } = useUbStorefrontState()
  const router = useRouter()
  const t = useTranslations("storefront")

  async function action(initialState: UpdateCompanyActionState, formData: FormData) {
    return updateCompanyAction(
      initialState,
      formData,
      props.accountContext.userData,
      props.accountContext.customerData
    ).then(async (state) => {
      if (!hasEntries(state.errors)) {
        setProfileUpdated(true)
        router.push(getLinkWithPrefixFromURLAndLocale("/account/profile", props.accountContext.sbLocale))
      }
      return {
        values: state.values,
        errors: state.errors,
      }
    })
  }

  const [state, formAction, isPending] = useActionState(action, {
    values: {
      companyName: props.accountContext.customerData?.companyName ?? "",
      companyName2: props.accountContext.customerData?.companyName2 ?? "",
      industry: props.accountContext.customerData?.industry ?? "",
      taxationID: props.accountContext.customerData?.taxationID ?? "",
      invoiceEmail:
        props.accountContext.userData?.invoiceEmail && props.accountContext.userData?.invoiceEmail.length > 0
          ? props.accountContext.userData.invoiceEmail
          : (props.accountContext.userData?.email ?? ""),
    },
    errors: {},
  })

  const { formState, register, reset, control } = useForm({
    values: state.values,
    errors: state.errors,
    resetOptions: {
      keepErrors: true,
    },
    mode: "onChange",
    resolver,
  })

  useEffect(() => {
    reset(state.values, {
      keepErrors: true,
    })
  }, [reset, state.values])

  return (
    <>
      <AccountBackLinkClient label={props.blok.label_common_back} />
      <div className={"relative flex w-full flex-col gap-2 lg:gap-6"}>
        <div className={"flex flex-col gap-2"}>
          <h2 className={"text-lg font-bold"}>{props.blok.title}</h2>
          <p>{props.blok.description}</p>
          <p className={"text-xs"}>{props.blok.required}</p>
        </div>

        <div>
          <Form action={formAction}>
            <div className="flex flex-col gap-2 lg:gap-6">
              <FormRow label={props.blok.company_name} htmlFor={"companyName"} required>
                <Input
                  {...register("companyName")}
                  className={cn("", { "border-primary": formState.errors.companyName })}
                />
                {formState.errors.companyName && (
                  <p className="mt-1 text-xs text-red-600">{props.blok.company_name_missing}</p>
                )}
              </FormRow>
              <FormRow label={props.blok.company_name_2} htmlFor={"companyName2"}>
                <Input
                  {...register("companyName2")}
                  className={cn("", { "border-primary": formState.errors.companyName2 })}
                />
              </FormRow>
              <FormRow label={props.blok.industry} htmlFor={"industry"}>
                <Controller
                  control={control}
                  name="industry"
                  render={({ field }) => (
                    <>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="industry"
                          className={cn("w-full rounded-lg border-gray-300 pl-3 text-left text-sm font-normal", {
                            "border-primary": formState.errors.industry,
                          })}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LINE_OF_BUSINESSES.map((code) => (
                            <SelectItem key={code} value={code}>
                              {t(`account.form.line_of_business.${code}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Ensure the value is submitted with native form post */}
                      <input type="hidden" name={field.name} value={field.value ?? ""} />
                    </>
                  )}
                />
              </FormRow>
              <FormRow label={props.blok.taxation_id} htmlFor={"taxationID"}>
                <Input
                  {...register("taxationID")}
                  className={cn("", { "border-primary": formState.errors.taxationID })}
                />
              </FormRow>
              <FormRow label={props.blok.invoice_email} htmlFor={"invoiceEmail"}>
                <Input
                  {...register("invoiceEmail")}
                  className={cn("", { "border-primary": formState.errors.invoiceEmail })}
                />
                {formState.errors.invoiceEmail && (
                  <p className="mt-1 text-xs text-red-600">{props.blok.invoice_email_invalid}</p>
                )}
              </FormRow>
              <div className="flex flex-wrap justify-between gap-2 lg:justify-end">
                <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending} className={"gap-1"}>
                  {!isPending ? (
                    <SaveIcon className={"inline size-5"} />
                  ) : (
                    <LoaderCircle className={"inline size-5 animate-spin"} />
                  )}
                  <span>{props.blok.submit}</span>
                </Button>

                <Button
                  type={"reset"}
                  variant="outline"
                  size={"defaultNoGrow"}
                  className={"gap-1"}
                  onClick={() => router.back()}
                >
                  <PlusIcon className={"inline size-5 rotate-45"} />
                  <span>{props.blok.cancel}</span>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        {props.blok.help && (
          <div>
            <UbRichText blok={{ content: props.blok.help, _uid: "", component: "ub_rich_text" }} />
          </div>
        )}
      </div>
    </>
  )
}
